/**
 * UIRegistry - Registry for UI slot injection.
 *
 * Content packs use this registry to:
 * 1. Define UI slots (named injection points in the layout)
 * 2. Inject React components into those slots
 *
 * The core pack defines slots; any pack can inject components.
 */

import { makeAutoObservable, runInAction } from 'mobx';

import type { ComponentType } from 'react';

/**
 * Context passed to UI condition functions.
 */
export interface UIContext {
  /** User's entitlements */
  entitlements: Set<string>;
  /** Any additional context */
  [key: string]: unknown;
}

/**
 * Configuration for a UI slot.
 */
export interface UISlotConfig {
  /** Unique slot identifier */
  id: string;
  /** Whether multiple components can be injected (default: true) */
  multiple?: boolean;
  /** Description of what this slot is for */
  description?: string;
}

/**
 * A component injection into a slot.
 */
export interface UIInjection {
  /** Unique injection identifier */
  id: string;
  /** Target slot ID */
  slot: string;
  /** React component to render */
  component: ComponentType<Record<string, unknown>>;
  /** Display order (lower = first, default: 0) */
  order?: number;
  /** Condition function - return true to show, false to hide */
  when?: (context: UIContext) => boolean;
}

/**
 * Internal slot storage.
 */
interface SlotStorage {
  config: UISlotConfig;
  injections: UIInjection[];
}

/**
 * UIRegistry manages UI component injection.
 *
 * @example
 * ```ts
 * const registry = new UIRegistry();
 *
 * // Core pack defines slots
 * registry.defineSlot({ id: 'sidebar-widgets', multiple: true });
 *
 * // Any pack can inject components
 * registry.inject({
 *   id: 'health-display',
 *   slot: 'sidebar-widgets',
 *   component: HealthDisplay,
 *   order: 0,
 * });
 *
 * // UI renders slot contents
 * const components = registry.getSlotComponents('sidebar-widgets');
 * ```
 */
export class UIRegistry {
  /** Slots by ID */
  private slots = new Map<string, SlotStorage>();

  /** Injections by ID (for quick lookup) */
  private injectionIndex = new Map<string, UIInjection>();

  /** Version counter for reactivity */
  version = 0;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Define a UI slot.
   *
   * @param config - Slot configuration
   * @throws Error if slot already exists
   */
  defineSlot(config: UISlotConfig): void {
    if (this.slots.has(config.id)) {
      throw new Error(`UI slot "${config.id}" is already defined`);
    }

    runInAction(() => {
      this.slots.set(config.id, {
        config: {
          ...config,
          multiple: config.multiple ?? true,
        },
        injections: [],
      });
      this.version++;
    });
  }

  /**
   * Check if a slot is defined.
   *
   * @param slotId - Slot ID
   */
  hasSlot(slotId: string): boolean {
    return this.slots.has(slotId);
  }

  /**
   * Get all defined slot IDs.
   */
  getSlotIds(): string[] {
    return Array.from(this.slots.keys());
  }

  /**
   * Inject a component into a slot.
   *
   * @param injection - Injection configuration
   * @throws Error if slot doesn't exist, injection ID exists, or slot is single and full
   */
  inject(injection: UIInjection): void {
    const slot = this.slots.get(injection.slot);

    if (!slot) {
      throw new Error(
        `UI slot "${injection.slot}" is not defined. ` +
          `Available slots: ${Array.from(this.slots.keys()).join(', ') || 'none'}`
      );
    }

    if (this.injectionIndex.has(injection.id)) {
      throw new Error(`UI injection "${injection.id}" already exists`);
    }

    if (!slot.config.multiple && slot.injections.length > 0) {
      throw new Error(
        `UI slot "${injection.slot}" only allows one injection. ` +
          `Already has: ${slot.injections[0]?.id ?? 'unknown'}`
      );
    }

    const normalizedInjection: UIInjection = {
      ...injection,
      order: injection.order ?? 0,
    };

    runInAction(() => {
      slot.injections.push(normalizedInjection);
      // Sort by order (ascending)
      slot.injections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      this.injectionIndex.set(injection.id, normalizedInjection);
      this.version++;
    });
  }

  /**
   * Remove an injection by ID.
   *
   * @param injectionId - Injection ID
   */
  remove(injectionId: string): void {
    const injection = this.injectionIndex.get(injectionId);
    if (!injection) {
      return;
    }

    const slot = this.slots.get(injection.slot);
    if (!slot) {
      return;
    }

    runInAction(() => {
      // Find by ID since MobX proxies may change object references
      const index = slot.injections.findIndex((inj) => inj.id === injectionId);
      if (index !== -1) {
        slot.injections.splice(index, 1);
      }
      this.injectionIndex.delete(injectionId);
      this.version++;
    });
  }

  /**
   * Get all injections for a slot.
   *
   * @param slotId - Slot ID
   * @param context - Optional context for conditional filtering
   * @returns Array of injections (empty if slot doesn't exist)
   */
  getSlotInjections(slotId: string, context?: UIContext): UIInjection[] {
    const slot = this.slots.get(slotId);
    if (!slot) {
      return [];
    }

    let injections = slot.injections;

    // Filter by condition if context provided
    if (context) {
      injections = injections.filter((injection) => {
        if (!injection.when) {
          return true;
        }
        return injection.when(context);
      });
    }

    return injections;
  }

  /**
   * Get components for a slot (convenience method).
   *
   * @param slotId - Slot ID
   * @param context - Optional context for conditional filtering
   * @returns Array of components
   */
  getSlotComponents(slotId: string, context?: UIContext): ComponentType<Record<string, unknown>>[] {
    return this.getSlotInjections(slotId, context).map((injection) => injection.component);
  }

  /**
   * Get injection count for a slot.
   *
   * @param slotId - Slot ID
   */
  injectionCount(slotId: string): number {
    return this.slots.get(slotId)?.injections.length ?? 0;
  }

  /**
   * Clear all injections from a slot.
   *
   * @param slotId - Slot ID
   */
  clearSlot(slotId: string): void {
    const slot = this.slots.get(slotId);
    if (!slot) {
      return;
    }

    runInAction(() => {
      for (const injection of slot.injections) {
        this.injectionIndex.delete(injection.id);
      }
      slot.injections = [];
      this.version++;
    });
  }

  /**
   * Reset the registry completely.
   */
  reset(): void {
    runInAction(() => {
      this.slots.clear();
      this.injectionIndex.clear();
      this.version++;
    });
  }
}
